/**
 * KikoBooks Environment Configuration Interface
 * 
 * This interface defines all environment-specific configuration properties.
 * All environment files (environment.ts, environment.dev.ts, environment.ts)
 * MUST implement this interface to ensure type safety and consistency.
 * 
 * @version 2.0
 * @lastUpdated 2026-02-04
 */
export interface IEnvironment {
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE SETTINGS
  // ═══════════════════════════════════════════════════════════════════════════

  /** Indicates if this is a production build */
  production: boolean;

  /** Environment name for logging/debugging */
  envName: 'local' | 'dev' | 'prod';

  /** Application version string */
  appVersion: string;

  /** Application theme name */
  appThemeName: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // API & URL CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  /** Backend API base URL (e.g., https://localhost:5001) */
  baseUrl: string;

  /** Frontend application URL (e.g., https://dev.kikobooks.com) */
  appUrl: string;

  /** Opposite support-replica environment URL; null disables direct local relay. */
  ticketReplicaPeerAppUrl: string | null;

  /** Website domain without protocol */
  website: string;

  /** URL for file fetching service */
  fileFetchUrl: string;

  /** Azure Blob Storage URL for file uploads */
  fileUrl: string;

  /** IdentityServer/Authorization Server URL (null if same as baseUrl) */
  tokenUrl: string | null;

  /** Login page route */
  loginUrl: string;

  /** Azure SignalR service base URL */
  azureSignalRBaseUrl: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTHENTICATION & SECURITY
  // ═══════════════════════════════════════════════════════════════════════════

  /** Local storage key for user authentication data */
  userDataKey: string;

  /** Security key for encryption operations */
  securityKey: string;

  /** Google reCAPTCHA site key (v2/v3). The secret key is server-only (Recaptcha:SecretKey). */
  reCaptchaSiteKey: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // STRIPE PAYMENT CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  /** Stripe publishable key for client-side integration */
  stripePublishableKey: string;

  /** Stripe lookup key for instant wallet recharge products */
  stripeInstantWalletLookup: string;

  /** Stripe lookup key for monthly wallet recharge products */
  stripeMonthlyWalletLookup: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // FEATURE FLAGS & TOGGLES
  // ═══════════════════════════════════════════════════════════════════════════

  /** Enable mock API responses for testing */
  isMockEnabled: boolean;

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBDOMAIN CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════════
  // MONITORING & TELEMETRY
  // ═══════════════════════════════════════════════════════════════════════════

  /** Azure Application Insights connection string (null to disable frontend telemetry) */
  appInsightsConnectionString: string | null;

  /** Default subdomain identifier */
  defaultSubDomain: string;

  /** Subdomain mapping configuration (hostname pattern -> subdomain name) */
  subDomainMappings: Record<string, string>;
}

/**
 * Helper function to get current subdomain based on hostname
 * @param environment - The environment configuration
 * @returns The matched subdomain or the default
 */
export function getSubDomain(environment: IEnvironment): string {
  if (typeof window === 'undefined') {
    return environment.defaultSubDomain;
  }

  const hostname = window.location.hostname;

  for (const [pattern, subdomain] of Object.entries(environment.subDomainMappings)) {
    if (hostname.includes(pattern)) {
      return subdomain;
    }
  }

  return environment.defaultSubDomain;
}
